/**
 * Documents Tab
 * 
 * File uploads and document management (stub for now)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload } from 'lucide-react';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
// no role gate needed for ownership; RLS enforces access
import { AssetDocumentList } from '@/components/instruments/document-management/AssetDocumentList';

interface DocumentsTabProps {
  asset: UnifiedAsset;
}

export function DocumentsTab({ asset }: DocumentsTabProps) {
  const [localDocuments, setLocalDocuments] = useState<any[]>([]);
  const [uid, setUid] = useState<string | null>(null);
  const assetId = String((asset as any)?.id);

  const { data: documents, refetch } = useQuery({
    queryKey: ['asset-documents', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asset_documents')
        .select('*')
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });
      if (error) {
        const status = (error as any)?.status;
        const msg = String((error as any)?.message || '').toLowerCase();
        if (status === 404 || msg.includes('does not exist') || msg.includes('not found')) {
          return [] as any[];
        }
        throw error;
      }
      return data || [];
    },
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUid(data?.session?.user?.id ?? null);
    }).catch(() => setUid(null));
  }, []);

  useEffect(() => {
    if (documents) setLocalDocuments(documents);
  }, [documents]);

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess?.session?.user?.id;
    if (!uid) { toast.error('You must be signed in to upload documents.'); return; }
    let success = 0;
    let failed = 0;
    for (const file of acceptedFiles) {
      try {
        const fileExt = file.name.split('.').pop();
        const filePath = `${uid}/${assetId}/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('asset_documents')
          .upload(filePath, file);
        if (uploadError) throw uploadError;

        const { data: newDoc, error: dbError } = await supabase
          .from('asset_documents')
          .insert({
            asset_id: assetId,
            user_id: uid,
            file_name: file.name,
            file_path: filePath,
            display_title: file.name,
          })
          .select()
          .single();
        if (dbError) {
          await supabase.storage.from('asset_documents').remove([filePath]);
          throw dbError;
        }
        setLocalDocuments(prev => [newDoc, ...prev]);
        success++;
      } catch (e: any) {
        failed++;
      }
    }
    if (success > 0) { toast.success(`Uploaded ${success} file${success > 1 ? 's' : ''}.${failed ? ` ${failed} failed.` : ''}`); await refetch(); }
    else { toast.error('There was an error uploading.'); }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    multiple: true,
    maxFiles: 10,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/30 hover:border-primary'}`}
          >
            <input {...getInputProps({ name: 'assetDocs', id: 'assetDocs', 'aria-label': 'Upload documents' })} />
            <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              {isDragActive ? 'Drop files here…' : 'Drag and drop documents (PDF, images), or click to select'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documents List (placeholder) */}
      <AssetDocumentList
        documents={localDocuments}
        canManageDocuments={!!uid && uid === (asset as any)?.user_id}
        onDocumentDeleted={async (deletedId) => {
          setLocalDocuments(prev => prev.filter(d => d.id !== deletedId));
          await refetch();
        }}
      />
    </div>
  );
}
